import React, { useState } from 'react';
import { useLang } from '../../contexts/LangContext';

interface AdjustServiceFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice?: any; // Invoice data to adjust
  onSave: (data: any) => Promise<void>;
}

const AdjustServiceFeeModal: React.FC<AdjustServiceFeeModalProps> = ({
  isOpen,
  onClose,
  invoice,
  onSave,
}) => {
  // const { t } = useLang(); // Removed unused variable
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form data for adjustment
  const [serviceName, setServiceName] = useState(invoice?.serviceName || '');
  const [billingPeriod, setBillingPeriod] = useState(invoice?.billingPeriod || '');
  const [currentAmount, setCurrentAmount] = useState(invoice?.currentAmount ?? invoice?.amount ?? 0);
  const [adjustmentType, setAdjustmentType] = useState('AMOUNT_CHANGE'); // AMOUNT_CHANGE or PARTIAL_PREPAYMENT
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [notes, setNotes] = useState('');

  React.useEffect(() => {
    if (invoice) {
      setServiceName(invoice.serviceName || '');
      setBillingPeriod(invoice.billingPeriod || '');
      setCurrentAmount(invoice.currentAmount ?? invoice.amount ?? 0);
      setAdjustmentAmount(''); // Reset adjustment amount when invoice changes
      setNotes('');
    }
  }, [invoice]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN').format(price);
  };

  const validateAdjustmentAmount = (amount: number, type: string) => {
    if (amount < 1) {
      return 'Số tiền điều chỉnh phải lớn hơn hoặc bằng 1 VND';
    }
    
    if (type === 'PARTIAL_PREPAYMENT' && amount >= currentAmount) {
      return 'Số tiền thanh toán trước phải nhỏ hơn tổng số tiền hóa đơn';
    }
    
    return null;
  };

  const calculateNewAmount = () => {
    const adjustment = parseFloat(adjustmentAmount) || 0;
    if (adjustmentType === 'AMOUNT_CHANGE') {
      return adjustment; // New total amount
    } else {
      return currentAmount - adjustment; // Remaining amount after prepayment
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const adjustment = parseFloat(adjustmentAmount) || 0;
    
    // Validation
    const validationError = validateAdjustmentAmount(adjustment, adjustmentType);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Additional validation for empty or invalid numbers
    if (!adjustmentAmount || adjustmentAmount.trim() === '' || isNaN(adjustment)) {
      setError('Vui lòng nhập số tiền hợp lệ');
      return;
    }

    setLoading(true);

    try {
      const data = {
        invoiceId: invoice?.id,
        adjustmentType: adjustmentType as 'AMOUNT_CHANGE' | 'PARTIAL_PREPAYMENT',
        adjustmentAmount: adjustment, // Make sure this is a number
        notes: notes || undefined // Convert empty string to undefined
      };

      console.log('Sending adjustment data:', data); // Debug log

      await onSave(data);
      onClose();
      // Reset form
      setAdjustmentType('AMOUNT_CHANGE');
      setAdjustmentAmount('');
      setNotes('');
    } catch (err: any) {
      console.error('Adjustment error:', err); // Debug log
      setError(err.message || 'Có lỗi xảy ra khi điều chỉnh hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAdjustmentType('AMOUNT_CHANGE');
    setAdjustmentAmount('');
    setNotes('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Điều chỉnh phí dịch vụ
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tên phí dịch vụ
            </label>
            <input
              type="text"
              value={serviceName}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kỳ hóa đơn
            </label>
            <input
              type="text"
              value={billingPeriod}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Số tiền
            </label>
            <input
              type="text"
              value={formatPrice(currentAmount)}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loại điều chỉnh *
            </label>
            <select
              value={adjustmentType}
              onChange={(e) => {
                setAdjustmentType(e.target.value);
                setAdjustmentAmount(''); // Reset amount when type changes
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="AMOUNT_CHANGE">Thay đổi số tiền</option>
              <option value="PARTIAL_PREPAYMENT">Thanh toán trước 1 phần</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {adjustmentType === 'AMOUNT_CHANGE' ? 'Số tiền mới *' : 'Số tiền thanh toán trước *'}
            </label>
            <input
              type="number"
              value={adjustmentAmount}
              onChange={(e) => setAdjustmentAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={adjustmentType === 'AMOUNT_CHANGE' ? 'Nhập số tiền mới' : 'Nhập số tiền đã thanh toán trước'}
              min="1"
              step="1"
              required
            />
            <p className="text-xs text-blue-600 mt-1">
              {adjustmentType === 'AMOUNT_CHANGE' 
                ? 'Nhập số tiền mới cần thay đổi. Số tiền này sẽ thay thế hoàn toàn số tiền cũ.' 
                : 'Nhập số tiền đã thanh toán trước. Sẽ tạo bản ghi thanh toán trong "Lịch sử thanh toán".'}
            </p>
          </div>

          {/* Preview section */}
          {parseFloat(adjustmentAmount) > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Xem trước điều chỉnh:</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Số tiền hiện tại:</span>
                  <span className="font-medium">{formatPrice(currentAmount)}</span>
                </div>
                {adjustmentType === 'AMOUNT_CHANGE' ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Số tiền mới:</span>
                      <span className="font-medium text-blue-600">{formatPrice(parseFloat(adjustmentAmount))}</span>
                    </div>
                    <div className="flex justify-between border-t pt-1">
                      <span className="text-gray-600">Chênh lệch:</span>
                      <span className={`font-medium ${parseFloat(adjustmentAmount) > currentAmount ? 'text-red-600' : 'text-green-600'}`}>
                        {parseFloat(adjustmentAmount) > currentAmount ? '+' : ''}{formatPrice(parseFloat(adjustmentAmount) - currentAmount)}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Đã thanh toán:</span>
                      <span className="font-medium text-green-600">{formatPrice(parseFloat(adjustmentAmount))}</span>
                    </div>
                    <div className="flex justify-between border-t pt-1">
                      <span className="text-gray-600">Còn lại:</span>
                      <span className="font-medium text-blue-600">{formatPrice(calculateNewAmount())}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ghi chú
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Lý do điều chỉnh, thông tin bổ sung..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            >
              {loading ? 'Đang lưu...' : 'Xác nhận điều chỉnh'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdjustServiceFeeModal; 